"use strict";

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * Copyright (c) 2020 Finn Thompson, licensed under the MIT License.
 *
 * This module implements form persistence across sessions via local storage.
 * * Register a form for persistence with `FormPersistence#persist(form[, options])`.
 * * Save a form to local storage with `FormPersistence#save(form[, options])`.
 * * Load a saved form (e.g. at window load time) with `FormPersistence#load(form[, options])`.
 * * Clear saved form data with `FormPersistence#clearStorage(form[, options])`.
 * * Serialize form data to an object with `FormPersistence#serialize(form[, options])`.
 * * Deserialize a data object into a form with `FormPersistence#deserialize(form, data[, options])`.
 *
 * See https://github.com/FThompson/FormPersistence.js
 */
var FormPersistence = function () {
  /**
   * Registers the given form for persistence by saving its data to local or session storage.
   * Saved form data will be stored upon page refresh and cleared upon form submission.
   * Saved form data will be loaded upon calling this function, typically on page load.
   *
   * @param {HTMLFormElement} form    The form to make persistent.
   * @param {Object}          options Options object containing any of the following:
   *  * uuid - A unique identifier for this form's storage key.
   *           Required if using a form without an id. If unspecified, form id will be used.
   *  * useSessionStorage - Use session storage if `true`, local storage if `false`. Default `false`.
   *  * saveOnSubmit - Save form data upon submit if `true`. Default `false`.
   *  * valueFunctions - Special value functions to apply, like `name: fn(form, value)`.
   *  * include - Define a whitelist array of data names to include.
   *  * exclude - Define a blacklist array of data names to exclude.
   *  * includeFilter - Define a whitelist filter function that inputs an element and outputs a boolean. The element
   *                    is included if the function returns true.
   *  * excludeFilter - Define a blacklist filter function that inputs an element and outputs a boolean. The element
   *                    is excluded if the function returns true.
   */
  function persist(form, options) {
    var defaults = {
      saveOnSubmit: false
    };
    var config = Object.assign({}, defaults, options);
    load(form, config); // Some devices like ios safari do not support beforeunload events.
    // Unload event does not work in some situations, so we use both unload/beforeunload
    // and remove the unload event if the beforeunload event fires successfully.
    // If problems persist, we can add listeners on the pagehide event as well.

    var saveForm = function saveForm() {
      return save(form, config);
    };

    var saveFormBeforeUnload = function saveFormBeforeUnload() {
      window.removeEventListener('unload', saveForm);
      saveForm();
    };

    window.addEventListener('beforeunload', saveFormBeforeUnload);
    window.addEventListener('unload', saveForm);

    if (!config.saveOnSubmit) {
      form.addEventListener('submit', function () {
        window.removeEventListener('beforeunload', saveFormBeforeUnload);
        window.removeEventListener('unload', saveForm);
        clearStorage(form, config);
      });
    }
  }
  /**
   * Serializes the given form into an object, excluding password and file inputs.
   *
   * @param {HTMLFormElement} form    The form to serialize.
   * @param {Object}          options Options object containing any of the following:
   *  * include - Define a whitelist array of data names to include.
   *  * exclude - Define a blacklist array of data names to exclude.
   *  * includeFilter - Define a whitelist filter function that inputs an element and outputs a boolean. The element
   *                    is included if the function returns true.
   *  * excludeFilter - Define a blacklist filter function that inputs an element and outputs a boolean. The element
   *                    is excluded if the function returns true.
   *
   * @return {Object} The serialized data object.
   */


  function serialize(form, options) {
    var defaults = {
      include: [],
      exclude: [],
      includeFilter: null,
      excludeFilter: null
    };
    var config = Object.assign({}, defaults, options);
    var data = {};

    var _iterator = _createForOfIteratorHelper(form.elements),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var element = _step.value;
        var tag = element.tagName;
        var type = element.type;

        if (tag === 'INPUT' && (type === 'password' || type === 'file')) {
          continue; // do not serialize passwords or files
        }

        if (isNameFiltered(element.name, config.include, config.exclude) || isElementFiltered(element, config.includeFilter, config.excludeFilter)) {
          continue;
        }

        if (tag === 'INPUT') {
          var _type = element.type;

          if (_type === 'radio') {
            if (element.checked) {
              pushToArray(data, element.name, element.value);
            }
          } else if (_type === 'checkbox') {
            pushToArray(data, element.name, element.checked);
          } else {
            pushToArray(data, element.name, element.value);
          }
        } else if (tag === 'TEXTAREA') {
          pushToArray(data, element.name, element.value);
        } else if (tag === 'SELECT') {
          if (element.multiple) {
            var _iterator2 = _createForOfIteratorHelper(element.options),
                _step2;

            try {
              for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
                var option = _step2.value;

                if (option.selected) {
                  pushToArray(data, element.name, option.value);
                }
              }
            } catch (err) {
              _iterator2.e(err);
            } finally {
              _iterator2.f();
            }
          } else {
            pushToArray(data, element.name, element.value);
          }
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return data;
  }
  /**
   * Add a value to an object, creating an array to place it in if needed.
   */


  function pushToArray(dict, key, value) {
    if (!(key in dict)) {
      dict[key] = [];
    }

    dict[key].push(value);
  }
  /**
   * Checks if the given name should be filtered out.
   */


  function isNameFiltered(name, include, exclude) {
    if (!name) {
      return true;
    }

    if (exclude.includes(name)) {
      return true;
    }

    if (include.length > 0 && !include.includes(name)) {
      return true;
    }

    return false;
  }
  /**
   * Checks if the given element should be filtered out, either by name or by predicate.
   */


  function isElementFiltered(element, includeFilter, excludeFilter) {
    if (excludeFilter && excludeFilter(element)) {
      return true;
    }

    if (includeFilter && !includeFilter(element)) {
      return true;
    }

    return false;
  }
  /**
   * Saves the given form to local or session storage.
   *
   * @param {HTMLFormElement} form    The form to serialize to local storage.
   * @param {Object}          options Options object containing any of the following:
   *  * uuid - A unique identifier for this form's storage key.
   *           Required if using a form without an id. If unspecified, form id will be used.
   *  * useSessionStorage - Use session storage if `true`, local storage if `false`. Default `false`.
   *  * include - Define a whitelist array of data names to include.
   *  * exclude - Define a blacklist array of data names to exclude.
   *  * includeFilter - Define a whitelist filter function that inputs an element and outputs a boolean. The element
   *                    is included if the function returns true.
   *  * excludeFilter - Define a blacklist filter function that inputs an element and outputs a boolean. The element
   *                    is excluded if the function returns true.
   */


  function save(form, options) {
    var defaults = {
      uuid: null,
      useSessionStorage: false
    };
    var config = Object.assign({}, defaults, options);
    var data = serialize(form, config);
    var storage = config.useSessionStorage ? sessionStorage : localStorage;
    storage.setItem(getStorageKey(form, config.uuid), JSON.stringify(data));
  }
  /**
   * Loads a given form by deserializing given data, optionally with given special value handling functions.
   *
   * @param {HTMLFormElement} form    The form to deserialize data into.
   * @param {Object}          data    The data object to deserialize into the form.
   * @param {Object}          options Options object containing any of the following:
   *  * valueFunctions - Special value functions to apply, like `name: fn(form, value)`.
   *  * include - Define a whitelist array of data names to include.
   *  * exclude - Define a blacklist array of data names to exclude.
   *  * includeFilter - Define a whitelist filter function that inputs an element and outputs a boolean. The element
   *                    is included if the function returns true.
   *  * excludeFilter - Define a blacklist filter function that inputs an element and outputs a boolean. The element
   *                    is excluded if the function returns true.
   */


  function deserialize(form, data, options) {
    var defaults = {
      valueFunctions: null,
      include: [],
      exclude: [],
      includeFilter: null,
      excludeFilter: null
    };
    var config = Object.assign({}, defaults, options); // apply given value functions first

    var speciallyHandled = [];

    if (config.valueFunctions !== null) {
      speciallyHandled = applySpecialHandlers(data, form, config);
    } // fill remaining values normally


    var _loop = function _loop(name) {
      if (isNameFiltered(name, config.include, config.exclude)) {
        return "continue";
      }

      if (!speciallyHandled.includes(name)) {
        var inputs = _toConsumableArray(form.elements).filter(function (element) {
          return element.name === name && !isElementFiltered(element, config.includeFilter, config.excludeFilter);
        });

        inputs.forEach(function (input, i) {
          applyValues(input, data[name], i);
        });
      }
    };

    for (var name in data) {
      var _ret = _loop(name);

      if (_ret === "continue") continue;
    }
  }
  /**
   * Loads a given form from local or session storage, optionally with given special value handling functions.
   * Does nothing if no saved values are found.
   *
   * @param {HTMLFormElement} form    The form to load saved values into.
   * @param {Object}          options Options object containing any of the following:
   *  * uuid - A unique identifier for this form's storage key.
   *           Required if using a form without an id. If unspecified, form id will be used.
   *  * useSessionStorage - Use session storage if `true`, local storage if `false`. Default `false`.
   *  * valueFunctions - Special value functions to apply, like `name: fn(form, value)`.
   *  * include - Define a whitelist array of data names to include.
   *  * exclude - Define a blacklist array of data names to exclude.
   *  * includeFilter - Define a whitelist filter function that inputs an element and outputs a boolean. The element
   *                    is included if the function returns true.
   *  * excludeFilter - Define a blacklist filter function that inputs an element and outputs a boolean. The element
   *                    is excluded if the function returns true.
   */


  function load(form, options) {
    var defaults = {
      uuid: null,
      useSessionStorage: false
    };
    var config = Object.assign({}, defaults, options);
    var storage = config.useSessionStorage ? sessionStorage : localStorage;
    var json = storage.getItem(getStorageKey(form, config.uuid));

    if (json) {
      var data = JSON.parse(json);
      deserialize(form, data, options);
    }
  }
  /**
   * Clears a given form's data from local or session storage.
   *
   * @param {HTMLFormElement} form              The form to clear stored data for.
   * @param {Object}          options Options object containing any of the following:
   *  * uuid - A unique identifier for this form's storage key.
   *           Required if using a form without an id. If unspecified, form id will be used.
   *  * useSessionStorage - Use session storage if `true`, local storage if `false`. Default `false`.
   */


  function clearStorage(form, options) {
    var defaults = {
      uuid: null,
      useSessionStorage: false
    };
    var config = Object.assign({}, defaults, options);
    var storage = config.useSessionStorage ? sessionStorage : localStorage;
    storage.removeItem(getStorageKey(form, config.uuid));
  }
  /**
   * Applies the given values to the given element.
   * Adds any checkbox elements checked to the given array.
   *
   * @param {HTMLElement} element The element to apply values to.
   * @param {Array} values        The array of values. Some element types use the first element instead of the index.
   * @param {Number} index        The index of the value array to apply if applicable.
   * @param {Array} checkedBoxes  The array of checkboxes to add any clicked checkboxes to.
   */


  function applyValues(element, values, index) {
    var tag = element.tagName;

    if (tag === 'INPUT') {
      var type = element.type;

      if (type === 'radio') {
        element.checked = element.value === values[0];
      } else if (type === 'checkbox') {
        element.checked = values[index];
      } else {
        element.value = values[index];
      }
    } else if (tag === 'TEXTAREA') {
      element.value = values[index];
    } else if (tag === 'SELECT') {
      if (element.multiple) {
        var _iterator3 = _createForOfIteratorHelper(element.options),
            _step3;

        try {
          for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
            var option = _step3.value;
            option.selected = values.includes(option.value);
          }
        } catch (err) {
          _iterator3.e(err);
        } finally {
          _iterator3.f();
        }
      } else {
        element.value = values[index];
      }
    }
  }
  /**
   * Runs given value handling functions in place of basic value insertion.
   *
   * Note that inclusion and exclusion filter functions do not apply here.
   *
   * @param {Object}          data           The form data being loaded.
   * @param {HTMLFormElement} form           The HTML form being loaded.
   * @param {Object}          valueFunctions The special value functions, like `name: fn(form, value)`.
   *
   * @return {Array} An array containing the data entry names that were handled.
   */


  function applySpecialHandlers(data, form, options) {
    var speciallyHandled = [];

    for (var fnName in options.valueFunctions) {
      if (fnName in data) {
        if (isNameFiltered(fnName, options.include, options.exclude)) {
          continue;
        }

        var _iterator4 = _createForOfIteratorHelper(data[fnName]),
            _step4;

        try {
          for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
            var value = _step4.value;
            options.valueFunctions[fnName](form, value);
          }
        } catch (err) {
          _iterator4.e(err);
        } finally {
          _iterator4.f();
        }

        speciallyHandled.push(fnName);
      }
    }

    return speciallyHandled;
  }
  /**
   * Creates a local storage key for the given form.
   *
   * @param {HTMLFormElement} form The form to create a storage key for.
   *
   * @return {String} The unique form storage key.
   * @throws {Error} If given a form without an id or uuid.
   */


  function getStorageKey(form, uuid) {
    if (!uuid && !form.id) {
      throw Error('form persistence requires a form id or uuid');
    }

    return 'form#' + (uuid ? uuid : form.id);
  }
  /**
   * Return the public interface of FormPersistence.
   */


  return {
    persist: persist,
    load: load,
    save: save,
    clearStorage: clearStorage,
    serialize: serialize,
    deserialize: deserialize
  };
}();
/**
 * Export the module if applicable.
 */


(function () {
  // istanbul ignore else
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = exports = FormPersistence;
  }
})();